<template>
    <div class="d-flex gap-4 flex-column">

        <StatsWidgets />

        <Card :header="$t('availabilityAnalysis')" overlay :loading="loading">

            <template #header>
                <div class="d-flex gap-2 align-items-center">
                    {{ $t('availabilityAnalysis') }}
                    <ButtonIcon
                        v-b-tooltip.hover.noninteractive.bottom
                        icon="download"
                        variant="light"
                        :icon-size="14"
                        class="p-1 rounded-max mb-auto width-25 height-25"
                        :title="$t('downloadTheEntireReport')"
                        :to="{ name: 'DataExport' }"
                    />
                </div>
            </template>

            <template #nav>
                <div :key="spaceId" class="d-flex gap-3 flex-column">
                    <div class="d-flex gap-3">
                        <MultiselectAvailableCampaigns :allow-empty="false" :value="campaigns" :disabled="loading" @changeTrackBy="campaigns = $event" @loading="campaignsLoading = $event" />
                        <Multiselect
                            v-show="showProductGroupsResults"
                            :value="groups"
                            :fetch-options-function="fetchSpaceGroups"
                            :placeholder="$t('productGroup')"
                            :selected-options-label="$t('selectedGroups')"
                            searchable
                            track-by="id"
                            label="name"
                            select-label=""
                            :disabled="loading"
                            show-checkboxes
                            show-selected
                            multiple
                            preselect-all
                            :close-on-select="false"
                            :clear-on-select="false"
                            class="width-200"
                            @changeTrackBy="groups = $event"
                            @loading="spaceGroupsLoading = $event"
                        />
                        <FormCategoryTreeSelect
                            v-show="!showProductGroupsResults"
                            ref="FormCategoryTreeSelect"
                            :value="categories"
                            :disabled="loading"
                            class="width-200"
                            preselect
                            prevent-empty-selection
                            :clearable="false"
                            @change="categories = $event"
                            @loading="categoriesLoading = $event"
                        />
                    </div>
                    <div class="d-flex gap-3 justify-content-end align-items-center">
                        <b-form-checkbox v-model="showProductGroupsResults" switch :disabled="loading">{{ $t('showResultsForProductGroups') }}</b-form-checkbox>
                    </div>
                </div>
            </template>

            <ErrorMessage :value="requestData.getError()" />

            <div class="dropdown-divider border-bottom border-light"></div>

            <div class="d-flex gap-4 flex-column">

                <OffersInTime />

                <div class="dropdown-divider border-bottom border-light"></div>

                <AverageAvailabilityOfProductsByStores />

                <div class="dropdown-divider border-bottom border-light"></div>

                <AverageAvailabilityByProducts />

                <div class="dropdown-divider border-bottom border-light"></div>

                <AvailabilityInTimeAccordingToStores />

            </div>

        </Card>

    </div>
</template>



<script>
    import FormCategoryTreeSelect from '@/components/common/FormCategoryTreeSelect.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import MultiselectAvailableCampaigns from '@/components/common/MultiselectAvailableCampaigns.vue';
    import { assignIfChanged } from '@/utils/util';
    import StatsWidgets from './StatsWidgets.vue';
    import OffersInTime from './OffersInTime.vue';
    import AverageAvailabilityOfProductsByStores from './AverageAvailabilityOfProductsByStores.vue';
    import AverageAvailabilityByProducts from './AverageAvailabilityByProducts.vue';
    import AvailabilityInTimeAccordingToStores from './AvailabilityInTimeAccordingToStores.vue';

    const DEFAULT_FILTERS_MAP = new Map();

    const DEFAULT_FILTERS = {
        showProductGroupsResults: false,
        campaigns: [],
        groups: [],
        categories: [],
    };

    export default {
        name: 'AvailabilityAnalysis',
        components: {
            Multiselect,
            FormCategoryTreeSelect,
            StatsWidgets,
            OffersInTime,
            AverageAvailabilityOfProductsByStores,
            AverageAvailabilityByProducts,
            AvailabilityInTimeAccordingToStores,
            MultiselectAvailableCampaigns,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        provide() {
            return {
                parentComponent: this,
            };
        },
        data() {
            return {
                requestData: this.$req('space/AvailabilityCharts'),
                requestDataCompare: this.$req('space/AvailabilityCharts'),
                requestSpaceGroups: this.$req('producerProductGroups/spaceGroups'),
                showProductGroupsResults: structuredClone(DEFAULT_FILTERS.showProductGroupsResults),
                campaigns: structuredClone(DEFAULT_FILTERS.campaigns),
                groups: structuredClone(DEFAULT_FILTERS.groups),
                categories: structuredClone(DEFAULT_FILTERS.categories),
                campaignsLoading: false,
                spaceGroupsLoading: false,
                categoriesLoading: false,
                waitingLoading: false,
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            timeRange() {
                return this.parentComponent.timeRange;
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const { startDate, endDate } = this.timeRange || {};
                const requestParams = { startDate, endDate };
                if (this.campaigns?.length) requestParams.campaigns = [...this.campaigns].sort().join(',');
                if (!this.showProductGroupsResults && this.categories?.length) requestParams.categories = [...this.categories].sort().join(',');
                if (this.showProductGroupsResults && this.groups?.length) requestParams.groups = [...this.groups].sort().join(',');
                return { urlParams, requestParams };
            },
            loadingFilters() {
                return this.campaignsLoading || this.spaceGroupsLoading || this.categoriesLoading;
            },
            loadingData() {
                return this.requestData.getLoading();
            },
            loading() {
                return this.loadingData || this.loadingFilters || this.waitingLoading;
            },
            data() {
                return this.requestData.getLoading() ? {} : this.requestData.getData() || {};
            },
        },
        watch: {
            loadingFilters(newVal, oldVal) {
                if (newVal === false && oldVal === true) {
                    Object.entries(DEFAULT_FILTERS).forEach(([key, val]) => {
                        this.setFilterValue(key, this[key]);
                    });
                }
            },
            spaceId() {
                this.initDefaultFilter();
                this.resetFilters();
            },
            apiParams: {
                immediate: true,
                handler(newVal, oldVal) {
                    if (JSON.stringify(newVal || {}) !== JSON.stringify(oldVal || {})) {
                        // we delay loadData and check if apiParams no longer changes
                        this.waitingLoading = true;
                        setTimeout(() => {
                            if (JSON.stringify(newVal || {}) === JSON.stringify(this.apiParams || {})) {
                                this.loadData();
                            }
                            this.waitingLoading = false;
                        }, 100);
                    }
                },
            },
        },
        methods: {
            initDefaultFilter() {
                if (!DEFAULT_FILTERS_MAP.has(this.spaceId)) {
                    DEFAULT_FILTERS_MAP.set(this.spaceId, structuredClone(DEFAULT_FILTERS));
                }
            },
            setFilterValue(filterName, filterValue) {
                const filters = DEFAULT_FILTERS_MAP.get(this.spaceId) || {};
                filters[filterName] = filterValue;
                DEFAULT_FILTERS_MAP.set(this.spaceId, filters);
            },
            getFilterValue(filterName) {
                const filters = DEFAULT_FILTERS_MAP.get(this.spaceId);
                return filters ? filters[filterName] : structuredClone(DEFAULT_FILTERS[filterName]);
            },
            loadData() {
                if (!this.loadingFilters) {
                    this.abortRequest();
                    this.fetchData();
                }
            },
            resetData() {
                this.requestData.resetAll();
                this.requestDataCompare.resetAll();
            },
            abortRequest() {
                this.requestData.abortAll();
                this.requestDataCompare.abortAll();
            },
            fetchData() {
                this.requestData.get(this.apiParams);
                this.requestDataCompare.get({
                    ...this.apiParams,
                    requestParams: {
                        ...this.apiParams.requestParams,
                        startDate: this.timeRange.compareStartDate,
                        endDate: this.timeRange.compareEndDate,
                    },
                });
            },
            fetchSpaceGroups() {
                return this.requestSpaceGroups.get({ urlParams: { spaceId: this.spaceId } }).then(data => data.items);
            },
            resetFilters() {
                Object.keys(DEFAULT_FILTERS).forEach(key => {
                    this[key] = assignIfChanged(this[key], structuredClone(this.getFilterValue(key)));
                });
                this.campaignsLoading = false;
                this.spaceGroupsLoading = false;
                this.categoriesLoading = false;
            },
        },
        created() {
            this.initDefaultFilter();
            this.parentComponent.$on('resetFilters', this.resetFilters);
        },
        beforeDestroy() {
            this.parentComponent.$off('resetFilters', this.resetFilters);
        },
    };
</script>
